import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS, Step } from 'react-joyride';

import { useCurrentUser } from 'src/store/user';
import { useJourneys } from 'src/store/journeys';
import { useCurrentOrganization } from 'src/store/organization';
import { useAppContext } from './joyride-context';
import { trackSegmentEvent } from 'src/utils/segment';
import { getMicrositeEditorSteps, getPitchEditorSteps, PitchJoyrideSteps, MicrositeJoyrideSteps } from './editor-joyride-steps';

import { Journey } from 'src/common/interfaces/journey.interface';
import { apiUpdateUser, apiCreateStep } from 'src/utils/journeyApi';
import { EventBus } from 'src/common/EventBus';

// Keys for storing tour state in localStorage
const TOUR_CONTINUATION_KEY = 'journey_editor_tour_continuation';
const TOUR_STEP_INDEX_KEY = 'journey_editor_tour_step_index';
const TOUR_EDITOR_TYPE_KEY = 'journey_editor_tour_type';

// Extend the Step interface to include our custom property
interface ExtendedStep extends Step {
  action?: string;
}

interface Props {
  initial: boolean;
  stepIndexNumber?: number,
  journey?: Journey
}

const JoyrideOnboardingGuide = ({initial, stepIndexNumber, journey}: Props) => {
  const { journeys } = useJourneys();
  const history = useHistory();
  const location = useLocation();

  const { currentOrganization } = useCurrentOrganization();
  const { currentUser, setCurrentUser } = useCurrentUser();

  // State to track current editor type
  const [editorType, setEditorType] = useState<'microsite' | 'pitch' | null>(null);
  // Store the previous URL to navigate back
  const [previousUrl, setPreviousUrl] = useState<string | null>(null);
  // Flag to track if we're in the middle of a page transition during the tour
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  // Flag to track if we're on the insights page
  const [isInsightsPage, setIsInsightsPage] = useState<boolean>(false);
  // Flag to track if we've already initialized the tour
  const tourInitializedRef = useRef<boolean>(false);
  // Refs to store timeout IDs for cleanup
  const insightsPageTimeoutRef = useRef<any | null>(null);
  const resumeTimeoutRef = useRef<any | null>(null);
  const insightsReadyTimeoutRef = useRef<any | null>(null);
  const insightsEmitReadyTimeoutRef = useRef<any | null>(null);

  const {
    setState,
    state: { run, stepIndex, steps, flowComplete, startedOnboarding, interrupt },
  } = useAppContext();


  const completedOnboarding = async(interrupted = false) => {    
    try {
      const params = {
        is_product_tour_complete: true,
      };

      const updatedUser = await apiUpdateUser(params);
      if (updatedUser) {
        setCurrentUser(updatedUser);
        if(interrupted) {
          trackSegmentEvent('Onboarding Tour - Force Closed', {})
        }
        else {
          trackSegmentEvent('Onboarding Tour - Completed', {});
        }
      }
    } catch (error: any) {
      trackSegmentEvent('Onboarding Tour - Complete Failed', {});
    }
  };

  // Check if we're on the insights page
  useEffect(() => {
    const onInsightsPage = location.pathname.includes('/insights/journeys/');
    setIsInsightsPage(onInsightsPage);
    
    // If we're on the insights page and have a continuation flag, prepare to resume
    if (onInsightsPage) {
      const continuation = localStorage.getItem(TOUR_CONTINUATION_KEY);
      const savedStepIndex = localStorage.getItem(TOUR_STEP_INDEX_KEY);
      const savedEditorType = localStorage.getItem(TOUR_EDITOR_TYPE_KEY);
      
      if (continuation === 'true' && savedStepIndex && savedEditorType && !tourInitializedRef.current && journey) {
        // Wait for DOM to be fully rendered
        insightsPageTimeoutRef.current = setTimeout(() => {
          try {
            const nextStepIndex = parseInt(savedStepIndex, 10);
            let newSteps: Step[] = [];
            
            if (savedEditorType === 'microsite') {
              newSteps = getMicrositeEditorSteps();
              setEditorType('microsite');
            } else {
              newSteps = getPitchEditorSteps();
              setEditorType('pitch');
            }
            
            tourInitializedRef.current = true;
            
            setState({
              run: true,
              stepIndex: nextStepIndex,
              startedOnboarding: true,
              steps: newSteps,
              flowComplete: false,
              interrupt: false
            });
            
            setIsNavigating(false);
            
            // Only clear localStorage after successful resumption
            setTimeout(() => {
              localStorage.removeItem(TOUR_CONTINUATION_KEY);
              localStorage.removeItem(TOUR_STEP_INDEX_KEY);
              localStorage.removeItem(TOUR_EDITOR_TYPE_KEY);
            }, 1000);
          } catch (error) {
            localStorage.removeItem(TOUR_CONTINUATION_KEY);
            localStorage.removeItem(TOUR_STEP_INDEX_KEY);
            localStorage.removeItem(TOUR_EDITOR_TYPE_KEY);
            setIsNavigating(false);
          }
        }, 1000);
      }
    }

    return () => {
      if (insightsPageTimeoutRef.current) {
        clearTimeout(insightsPageTimeoutRef.current);
      }
    };
  }, [location.pathname, journey, setState]);

  // First check for continuation flag when component mounts
  useEffect(() => {
    const continuation = localStorage.getItem(TOUR_CONTINUATION_KEY);
    if (continuation === 'true') {
      setIsNavigating(true);
    }
  }, []);

  // Listen for the ready-to-resume event from EditorJourneyEdit
  useEffect(() => {
    const handleReadyToResume = () => {
      // If we have a continuation flag, we should resume the tour
      const continuation = localStorage.getItem(TOUR_CONTINUATION_KEY);
      if (continuation === 'true') {
        const savedStepIndex = localStorage.getItem(TOUR_STEP_INDEX_KEY);
        const savedEditorType = localStorage.getItem(TOUR_EDITOR_TYPE_KEY);
        
        if (savedStepIndex && savedEditorType) {
          try {
            const nextStepIndex = parseInt(savedStepIndex, 10);
            // Determine the correct steps array based on saved editor type
            let newSteps: Step[] = [];
            
            if (savedEditorType === 'microsite') {
              newSteps = getMicrositeEditorSteps();
              setEditorType('microsite');
            } else {
              newSteps = getPitchEditorSteps();
              setEditorType('pitch');
            }
            
            // Restore the tour state with a delay to ensure the DOM is fully ready
            resumeTimeoutRef.current = setTimeout(() => {
              // Mark the tour as initialized to prevent re-initialization
              tourInitializedRef.current = true;
              
              // Build fresh state with the new steps array and saved index
              setState({
                run: true,
                stepIndex: nextStepIndex,
                startedOnboarding: true,
                steps: newSteps,
                flowComplete: false,
                interrupt: false
              });
              
              // Clear the navigation flags
              setIsNavigating(false);
              
              // Clear the localStorage keys after restoration
              localStorage.removeItem(TOUR_CONTINUATION_KEY);
              localStorage.removeItem(TOUR_STEP_INDEX_KEY);
              localStorage.removeItem(TOUR_EDITOR_TYPE_KEY);
            }, 1000);
          } catch (error) {
            localStorage.removeItem(TOUR_CONTINUATION_KEY);
            localStorage.removeItem(TOUR_STEP_INDEX_KEY);
            localStorage.removeItem(TOUR_EDITOR_TYPE_KEY);
            setIsNavigating(false);
          }
        } else {
          setIsNavigating(false);
          localStorage.removeItem(TOUR_CONTINUATION_KEY);
          localStorage.removeItem(TOUR_STEP_INDEX_KEY);
          localStorage.removeItem(TOUR_EDITOR_TYPE_KEY);
        }
      }
    };

    // Listen for insights page ready event
    const handleInsightsReady = () => {
      const continuation = localStorage.getItem(TOUR_CONTINUATION_KEY);
      const savedStepIndex = localStorage.getItem(TOUR_STEP_INDEX_KEY);
      const savedEditorType = localStorage.getItem(TOUR_EDITOR_TYPE_KEY);
      
      if (continuation === 'true' && savedStepIndex && savedEditorType) {
        try {
          const nextStepIndex = parseInt(savedStepIndex, 10);
          let newSteps: Step[] = [];
          
          if (savedEditorType === 'microsite') {
            newSteps = getMicrositeEditorSteps();
            setEditorType('microsite');
          } else {
            newSteps = getPitchEditorSteps();
            setEditorType('pitch');
          }
          
          // Use a shorter delay for insights page since it's a simpler view
          insightsReadyTimeoutRef.current = setTimeout(() => {
            tourInitializedRef.current = true;
            
            setState({
              run: true,
              stepIndex: nextStepIndex,
              startedOnboarding: true,
              steps: newSteps,
              flowComplete: false,
              interrupt: false
            });
            
            setIsNavigating(false);
          }, 500);
        } catch (error) {
          console.error("Error resuming tour on insights page:", error);
          setIsNavigating(false);
        }
      }
    };

    // Set up event listeners
    EventBus.on('onboarding:ready-to-resume', handleReadyToResume);
    EventBus.on('insights:page-ready', handleInsightsReady);

    // Clean up
    return () => {
      EventBus.off('onboarding:ready-to-resume', handleReadyToResume);
      EventBus.off('insights:page-ready', handleInsightsReady);
      
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
      
      if (insightsReadyTimeoutRef.current) {
        clearTimeout(insightsReadyTimeoutRef.current);
      }
    };
  }, [setState]);

  // Check if user is in an editor
  useEffect(() => {
    // Skip this check if we're in the middle of navigation
    if (isNavigating) return;
    
    // Check if location path includes editor paths
    const isMicrositeEditor = location.pathname.includes('/journeys/neue-edit/');
    const isPitchEditor = location.pathname.includes('/journeys/edit/');
    
    if ((isMicrositeEditor || isPitchEditor)) {
      // Determine editor type based on journey data
      if (journey?.data?.neue === true) {
        setEditorType('microsite');
      } else {
        setEditorType('pitch');
      }
    }
  }, [location.pathname, journey, isNavigating]);

  // Save the current location whenever it changes
  useEffect(() => {
    if (location) {
      setPreviousUrl(location.pathname);
    }
  }, [location]);

  const initiateProductOnboardingTour = () => {
    // Skip initialization if we're navigating or the tour is already initialized
    if (isNavigating || tourInitializedRef.current) {
      return;
    }

    // For users who haven't completed the tour
    if (!flowComplete && currentUser && !currentUser.is_product_tour_complete && !interrupt) {
      // Mark as initialized
      tourInitializedRef.current = true;
      
      // Determine editor type based on journey data if editorType is null
      let tourEditorType = editorType;
      if (!tourEditorType && journey) {
        tourEditorType = journey.data?.neue === true ? 'microsite' : 'pitch';
      }
      
      if (tourEditorType) {
        // If we have determined the editor type, show appropriate tour
        const editorSteps = tourEditorType === 'microsite' 
          ? getMicrositeEditorSteps() 
          : getPitchEditorSteps();
        
        setState({
          run: true,
          stepIndex: 0,
          startedOnboarding: true,
          steps: editorSteps,
          flowComplete: false
        });
        

        trackSegmentEvent('Onboarding Tour - Loaded', {});
      }
    }
  }

  useEffect(() => {
    // Check for continuation flag
    const continuation = localStorage.getItem(TOUR_CONTINUATION_KEY);
    if (continuation === 'true') {
      // Don't initialize if we're resuming
      return;
    }
    
    // If stepIndexNumber is explicitly set to -1, don't initialize tour
    if (stepIndexNumber === -1) {
      return;
    }
    
    // Otherwise initialize normally if not navigating and user is logged in
    if (!isNavigating && currentUser) {
      initiateProductOnboardingTour();
    }
  }, [editorType, isNavigating, stepIndexNumber, currentUser, journeys]);

  const handleJoyrideCallback = async(data: CallBackProps) => {
    const { status, index, step, action, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    const organizationSlug = currentOrganization?.slug;



    // Handle skip action directly
    if(action === 'skip') {
      setState(prevState => ({ ...prevState, run: false, interrupt: true, flowComplete: true }));
      // Make sure to await the API call
      await completedOnboarding(true);
      return;
    }

    if(action === ACTIONS.NEXT && type === EVENTS.TOUR_END) {
      setState(prevState => ({ ...prevState, run: false, flowComplete: true }));
      await completedOnboarding(false);
      return;
    }

    // Handle close button
    if(action === 'close') {
      return;
    }

    // Editor-specific tour logic
    if (editorType) {
      if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
        const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        
        // Check if we're moving from the "Add Step" step to the "Upload files & documents" step
        if (editorType === 'pitch' && 
            index === PitchJoyrideSteps.ADD_STEP && 
            action === ACTIONS.NEXT && 
            type === EVENTS.STEP_AFTER) {
          
          // Check if the current step has the custom action property
          const currentStep = steps[index] as ExtendedStep;
          if (currentStep && currentStep.action === 'onNewStepClicked') {
            // Execute the same logic as onNewStepClicked
            EventBus.emit('editor:reset-stage', { resetEditor: true, showInitialStage: true });
            
            if (journey) {
              let stepToAdd = journey.steps && journey.steps.length > 0 ? 
                journey.steps[journey.steps.length - 1] : null;
                
              if (!stepToAdd) {
                // Create a new step if none exists
                stepToAdd = await apiCreateStep(journey.uuid);
              }
              
              // Only emit the event if stepToAdd exists
              if (stepToAdd) {
                EventBus.emit('editor:navigate-to-content-new', { stepId: stepToAdd.id });
              }
            }
          }
        }

        // Check if we're moving from "Upload files & documents" to "Share Your Pitch"
        if (editorType === 'pitch' && 
            index === PitchJoyrideSteps.CUSTOMIZE_LINK && 
            action === ACTIONS.NEXT && 
            type === EVENTS.STEP_AFTER) {
          
          // First navigate back to the previous page (main editor page)
          if (journey) {
            // Store only the necessary data - don't try to serialize the steps
            localStorage.setItem(TOUR_STEP_INDEX_KEY, nextStepIndex.toString());
            localStorage.setItem(TOUR_EDITOR_TYPE_KEY, editorType);
            localStorage.setItem(TOUR_CONTINUATION_KEY, 'true');
            
            // Set navigating flag to prevent tour from re-initializing improperly
            setIsNavigating(true);
            
            // Temporarily pause the tour
            setState(prevState => ({...prevState, run: false}));
            
            // Navigate back to the main journey editor page
            const journeyId = journey.uuid;
            
            // Navigate back to the main editor page
            history.push(`/${organizationSlug}/journeys/edit/${journeyId}`);
            
            return; // Return early to prevent default step index update
          }
        }
        
        // Navigate to insights page after Insights Button step for both editor types
        if ((editorType === 'microsite' && index === MicrositeJoyrideSteps.INSIGHTS_BUTTON && 
            action === ACTIONS.NEXT && type === EVENTS.STEP_AFTER) ||
            (editorType === 'pitch' && index === PitchJoyrideSteps.INSIGHTS_BUTTON && 
            action === ACTIONS.NEXT && type === EVENTS.STEP_AFTER)) {
          
          if (journey) {
            // Store tour state
            localStorage.setItem(TOUR_STEP_INDEX_KEY, nextStepIndex.toString());
            localStorage.setItem(TOUR_EDITOR_TYPE_KEY, editorType);
            localStorage.setItem(TOUR_CONTINUATION_KEY, 'true');
            
            // Set navigating flag
            setIsNavigating(true);
            
            // Pause the tour
            setState(prevState => ({...prevState, run: false}));
            
            // Navigate to insights page
            const journeyId = journey.uuid;
            

            
            
            history.push(`/${organizationSlug}/insights/journeys/${journeyId}`);
            
            return; 
          }
        }
        
        // Return from insights to editor when going back
        if ((editorType === 'microsite' && index === MicrositeJoyrideSteps.INSIGHTS_PAGE && 
            action === ACTIONS.PREV && type === EVENTS.STEP_AFTER) ||
            (editorType === 'pitch' && index === PitchJoyrideSteps.INSIGHTS_PAGE && 
            action === ACTIONS.PREV && type === EVENTS.STEP_AFTER)) {
          
          if (journey) {
            // Store tour state
            localStorage.setItem(TOUR_STEP_INDEX_KEY, nextStepIndex.toString());
            localStorage.setItem(TOUR_EDITOR_TYPE_KEY, editorType);
            localStorage.setItem(TOUR_CONTINUATION_KEY, 'true');
            
            // Set navigating flag
            setIsNavigating(true);
            
            // Pause the tour
            setState(prevState => ({...prevState, run: false}));
            
            const journeyId = journey.uuid;
            
            if (editorType === 'microsite') {
              history.push(`/${organizationSlug}/journeys/neue-edit/${journeyId}`);
            } else {
              history.push(`/${organizationSlug}/journeys/edit/${journeyId}`);
            }
            
            return; 
          }
        }
        
        // Only update step index if we're not navigating
        if (!isNavigating) {
          setState(prevState => ({...prevState, stepIndex: nextStepIndex}));
        }
      }
    }
    
    if (finishedStatuses.includes(status)) {
      setState(prevState => ({ ...prevState, run: false, flowComplete: true }));
      
      await completedOnboarding(status === STATUS.SKIPPED);
    }
  };

  // Check if we're on the insights page and emit ready event
  useEffect(() => {
    const isInsightsPage = location.pathname.includes('/insights/journeys/');
    if (isInsightsPage && isNavigating) {
      insightsEmitReadyTimeoutRef.current = setTimeout(() => {
        EventBus.emit('insights:page-ready');
      }, 300);
    }

    return () => {
      if (insightsEmitReadyTimeoutRef.current) {
        clearTimeout(insightsEmitReadyTimeoutRef.current);
      }
    };
  }, [location.pathname, isNavigating]);

  return (
    <div className='100vh relative'>
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        run={run}
        scrollToFirstStep
        // showProgress
        showSkipButton
        steps={steps}
        stepIndex={stepIndex}
        disableOverlayClose={true}
        spotlightPadding={0}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#1A1F71',
            overlayColor: isInsightsPage ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.2)',
          },
          buttonClose: {
            display: 'none'
          },
          spotlight: {
            backgroundColor: 'transparent',
          }
        }}
        locale={{
          skip: "Skip tour",
          last: "End tour"
        }}

        disableCloseOnEsc={false}
        disableOverlay={false}
        hideBackButton={false}
      />
    </div> 
  );
}

export default JoyrideOnboardingGuide